import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
	CartesianGrid,
	Legend,
	Line,
	LineChart,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtNumber,
	input,
	Panel,
	Spinner,
} from "../components/flows/ui";
import { PageHeader } from "../components/layout/PageHeader";
import type {
	CompetitionOverview,
	DomainPoint,
	DomainSeries,
	GscTotals,
	Initiative,
	KeywordList,
	KeywordRow,
	Panel as PanelKey,
	PanelRow,
	SectionsReport,
} from "../types/competition";

const PANEL_LABELS: Record<PanelKey, string> = {
	product_line: "Product lines · W1",
	category: "Categories · W2/W5",
	brand_category: "Brand × category · W4",
	tilbud: "Tilbud · W6",
	product: "Exact products · W7",
	merchant: "Our shops · W9",
	control: "Control",
};

const PANEL_COLORS: Record<PanelKey, string> = {
	product_line: "#2563eb",
	category: "#16a34a",
	brand_category: "#9333ea",
	tilbud: "#f59e0b",
	product: "#0891b2",
	merchant: "#db2777",
	control: "#64748b",
};

const SECTION_COLORS = [
	"#2563eb",
	"#16a34a",
	"#9333ea",
	"#f59e0b",
	"#0891b2",
	"#db2777",
	"#64748b",
	"#dc2626",
	"#65a30d",
];

const PANEL_KEYS = Object.keys(PANEL_LABELS) as PanelKey[];

const field = input.replace("w-full ", "");

const pct = (v: number | null | undefined, digits = 0) =>
	v === null || v === undefined ? "—" : `${(v * 100).toFixed(digits)}%`;

const pts = (v: number | null | undefined) =>
	v === null || v === undefined
		? "—"
		: `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)} pt`;

const rank = (v: number | null | undefined) =>
	v === null || v === undefined ? "30+" : String(Math.round(v * 10) / 10);

const compact = (v: number | null | undefined) =>
	v === null || v === undefined
		? "—"
		: v.toLocaleString("da-DK", {
				notation: v >= 100_000 ? "compact" : "standard",
				maximumFractionDigits: v >= 100_000 ? 1 : 0,
			});

const fmtShortDate = (iso: string) =>
	`${Number(iso.slice(8, 10))}/${Number(iso.slice(5, 7))}`;

const weekOf = (iso: string) => {
	const d = new Date(`${iso}T00:00:00Z`);
	d.setUTCDate(d.getUTCDate() - ((d.getUTCDay() + 6) % 7));
	return d.toISOString().slice(0, 10);
};

const Change = ({
	now,
	then,
	invert,
}: {
	now: number | null | undefined;
	then: number | null | undefined;
	invert?: boolean;
}) => {
	if (now == null || then == null || now === then) return null;
	const diff = now - then;
	const good = invert ? diff < 0 : diff > 0;
	const rel =
		then !== 0
			? ` (${diff > 0 ? "+" : ""}${((diff / then) * 100).toFixed(0)}%)`
			: "";
	return (
		<span
			className={`ml-2 rounded px-1.5 py-0.5 text-[11px] font-medium ${good ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}
		>
			{diff > 0 ? "+" : ""}
			{compact(diff)}
			{rel}
		</span>
	);
};

const METRICS: Array<{
	key: keyof DomainPoint;
	label: string;
	invert?: boolean;
	selfOnly?: boolean;
}> = [
	{ key: "organicKeywords", label: "Organic keywords" },
	{ key: "top10", label: "Keywords in top 10" },
	{ key: "organicEtv", label: "Est. traffic / month" },
	{ key: "referringDomains", label: "Referring domains" },
	{ key: "brokenPages", label: "Broken pages with links", invert: true },
	{ key: "sharedKeywords", label: "Keywords shared with us" },
];

const ScoreCard = ({
	metric,
	domains,
}: {
	metric: (typeof METRICS)[number];
	domains: DomainSeries[];
}) => {
	const self = domains.find((d) => d.isSelf);
	const value = (p: DomainPoint | null) =>
		p ? (p[metric.key] as number | null) : null;
	const showSelf = metric.key !== "sharedKeywords";
	return (
		<div className="card bg-white p-5">
			<div className="text-[12px] font-medium uppercase tracking-wide text-slate-500">
				{metric.label}
			</div>
			{showSelf && self && (
				<div className="mt-2 flex items-baseline text-2xl font-semibold text-slate-800">
					{compact(value(self.latest))}
					<Change
						now={value(self.latest)}
						then={value(self.baseline)}
						invert={metric.invert}
					/>
				</div>
			)}
			{showSelf && (
				<div className="text-[11px] text-slate-400">
					PriceOnline · baseline {compact(value(self?.baseline ?? null))}
				</div>
			)}
			<dl className="mt-3 grid grid-cols-2 gap-y-1 text-[12px]">
				{domains
					.filter((d) => !d.isSelf)
					.map((d) => (
						<div key={d.domain} className="contents">
							<dt className="text-slate-500">{d.label}</dt>
							<dd className="text-right tabular-nums text-slate-700">
								{compact(value(d.latest))}
							</dd>
						</div>
					))}
			</dl>
		</div>
	);
};

const GscCell = ({ now, then }: { now: GscTotals; then: GscTotals }) => (
	<span className="tabular-nums">
		{fmtNumber(then.clicks)} → <strong>{fmtNumber(now.clicks)}</strong>
		<span className="ml-1 text-[11px] text-slate-400">
			({compact(now.impressions)} impr.)
		</span>
	</span>
);

const PanelTable = ({
	rows,
	onPick,
}: {
	rows: PanelRow[];
	onPick: (panel: PanelKey) => void;
}) => (
	<div className="overflow-x-auto">
		<table className="w-full text-[13px]">
			<thead>
				<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
					<th className="py-2 pr-4 font-medium">Panel</th>
					<th className="py-2 pl-3 text-right font-medium">Keywords</th>
					<th className="py-2 pl-3 text-right font-medium">Top 10</th>
					<th className="py-2 pl-3 text-right font-medium">Lift vs control</th>
					<th className="py-2 pl-3 text-right font-medium">Top 3</th>
					<th className="py-2 pl-3 text-right font-medium">Median rank</th>
					<th className="py-2 pl-3 text-right font-medium">
						PriceRunner top 10
					</th>
					<th className="py-2 pl-3 text-right font-medium">Ahead of PR</th>
					<th className="py-2 pl-3 text-right font-medium">In grid</th>
					<th className="py-2 pl-3 text-right font-medium">Right page</th>
					<th className="py-2 pl-3 text-right font-medium">GSC clicks 28d</th>
				</tr>
			</thead>
			<tbody>
				{rows.map((r) => (
					<tr
						key={r.panel}
						className={`cursor-pointer border-b border-hairline last:border-0 hover:bg-canvas ${r.panel === "control" ? "text-slate-500" : ""}`}
						onClick={() => onPick(r.panel)}
					>
						<td className="py-2 pr-4">
							<span className="inline-flex items-center gap-2">
								<span
									className="h-2.5 w-2.5 rounded-sm"
									style={{ background: PANEL_COLORS[r.panel] }}
								/>
								{PANEL_LABELS[r.panel]}
							</span>
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{fmtNumber(r.keywords)}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{r.latest ? (
								<>
									{pct(r.baseline?.top10Share, 1)} →{" "}
									<strong>{pct(r.latest.top10Share, 1)}</strong>
								</>
							) : (
								"not checked yet"
							)}
						</td>
						<td
							className={`py-2 pl-3 text-right tabular-nums ${r.lift === null ? "" : r.lift > 0 ? "text-emerald-700" : r.lift < 0 ? "text-red-700" : ""}`}
						>
							{pts(r.lift)}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{pct(r.latest?.top3Share, 1)}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{r.latest ? rank(r.latest.medianRank) : "—"}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{pct(r.latest?.pricerunnerTop10Share)}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{r.latest ? fmtNumber(r.latest.aheadOfPricerunner) : "—"}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{pct(r.latest?.gridShare)}
						</td>
						<td className="py-2 pl-3 text-right tabular-nums">
							{r.latest && r.latest.landingChecked > 0
								? `${r.latest.landingCorrect}/${r.latest.landingChecked}`
								: r.withTarget === 0
									? "no targets"
									: "—"}
						</td>
						<td className="py-2 pl-3 text-right">
							<GscCell now={r.gscCurrent} then={r.gscBaseline} />
						</td>
					</tr>
				))}
			</tbody>
		</table>
	</div>
);

const TrendChart = ({ overview }: { overview: CompetitionOverview }) => {
	const data = useMemo(() => {
		const byWeek = new Map<string, Record<string, number | string>>();
		for (const p of overview.trend) {
			const row = byWeek.get(p.week) ?? { week: p.week };
			row[p.panel] = Math.round(p.top10Share * 1000) / 10;
			byWeek.set(p.week, row);
		}
		return [...byWeek.values()].sort((a, b) =>
			String(a.week).localeCompare(String(b.week)),
		);
	}, [overview.trend]);
	const shipped = overview.initiatives.filter((i) => i.shippedAt);

	if (data.length === 0)
		return (
			<Empty>
				No SERP checks yet. They arrive with the first weekly track_competitors
				run.
			</Empty>
		);
	return (
		<div className="h-72">
			<ResponsiveContainer width="100%" height="100%">
				<LineChart data={data}>
					<CartesianGrid
						strokeDasharray="3 3"
						vertical={false}
						stroke="#e5e7eb"
					/>
					<XAxis
						dataKey="week"
						tickFormatter={fmtShortDate}
						tick={{ fontSize: 11 }}
					/>
					<YAxis tick={{ fontSize: 11 }} width={40} unit="%" />
					<Tooltip formatter={(v: number) => `${v}%`} />
					<Legend wrapperStyle={{ fontSize: 12 }} />
					{shipped.map((i) => (
						<ReferenceLine
							key={i.id}
							x={weekOf(i.shippedAt as string)}
							stroke="#94a3b8"
							strokeDasharray="4 4"
							label={{ value: i.workstream, fontSize: 10, fill: "#64748b" }}
						/>
					))}
					{PANEL_KEYS.map((p) => (
						<Line
							key={p}
							type="monotone"
							dataKey={p}
							name={PANEL_LABELS[p]}
							stroke={PANEL_COLORS[p]}
							strokeDasharray={p === "control" ? "5 3" : undefined}
							dot={data.length < 3}
							connectNulls
						/>
					))}
				</LineChart>
			</ResponsiveContainer>
		</div>
	);
};

const SectionsPanel = ({ report }: { report: SectionsReport }) => {
	const sections = report.rows.map((r) => r.section);
	const data = useMemo(() => {
		const byWeek = new Map<string, Record<string, number | string>>();
		for (const s of report.series) {
			const row = byWeek.get(s.week) ?? { week: s.week };
			row[s.section] = s.clicks;
			byWeek.set(s.week, row);
		}
		return [...byWeek.values()].sort((a, b) =>
			String(a.week).localeCompare(String(b.week)),
		);
	}, [report.series]);

	return (
		<div className="space-y-5">
			{data.length > 0 && (
				<div className="h-64">
					<ResponsiveContainer width="100%" height="100%">
						<LineChart data={data}>
							<CartesianGrid
								strokeDasharray="3 3"
								vertical={false}
								stroke="#e5e7eb"
							/>
							<XAxis
								dataKey="week"
								tickFormatter={fmtShortDate}
								tick={{ fontSize: 11 }}
							/>
							<YAxis tick={{ fontSize: 11 }} width={56} />
							<Tooltip formatter={(v: number) => fmtNumber(v)} />
							<Legend wrapperStyle={{ fontSize: 12 }} />
							{sections.map((s, i) => (
								<Line
									key={s}
									type="monotone"
									dataKey={s}
									name={`${s} clicks`}
									stroke={SECTION_COLORS[i % SECTION_COLORS.length]}
									dot={false}
								/>
							))}
						</LineChart>
					</ResponsiveContainer>
				</div>
			)}
			<div className="overflow-x-auto">
				<table className="w-full text-[13px]">
					<thead>
						<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
							<th className="py-2 pr-4 font-medium">Section</th>
							<th className="py-2 pl-3 text-right font-medium">Impressions</th>
							<th className="py-2 pl-3 text-right font-medium">Clicks</th>
							<th className="py-2 pl-3 text-right font-medium">CTR</th>
							<th className="py-2 pl-3 text-right font-medium">Avg. pos</th>
							<th className="py-2 pl-3 text-right font-medium">
								Pos 1–3 share
							</th>
							<th className="py-2 pl-3 text-right font-medium">Pos 1–3 CTR</th>
						</tr>
					</thead>
					<tbody>
						{report.rows.map((r) => {
							const top = r.bands.find((b) => b.band === "1-3");
							return (
								<tr
									key={r.section}
									className="border-b border-hairline last:border-0"
								>
									<td className="py-2 pr-4 font-mono text-[12px]">
										{r.section}
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{compact(r.current.impressions)}
										<Change
											now={r.current.impressions}
											then={r.baseline.impressions}
										/>
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{fmtNumber(r.current.clicks)}
										<Change now={r.current.clicks} then={r.baseline.clicks} />
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{pct(r.current.ctr, 2)}
										<span className="ml-1 text-[11px] text-slate-400">
											({pct(r.baseline.ctr, 2)})
										</span>
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{r.current.avgPosition?.toFixed(1) ?? "—"}
										<span className="ml-1 text-[11px] text-slate-400">
											({r.baseline.avgPosition?.toFixed(1) ?? "—"})
										</span>
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{pct(
											r.current.impressions
												? (top?.impressions ?? 0) / r.current.impressions
												: null,
										)}
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{pct(top?.ctr ?? null, 2)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			<p className="text-[11px] text-slate-400">
				Last 28 days of Search Console ({report.windows.gscCurrent?.from} →{" "}
				{report.windows.gscCurrent?.to}); bracketed figures and the change are
				against the baseline window {report.windows.gscBaseline.from} →{" "}
				{report.windows.gscBaseline.to}. Search Console cannot tell a grid
				impression from an organic one, so on <code>/p</code> and <code>/</code>{" "}
				positions 1–3 are in practice the comparison grid.
			</p>
		</div>
	);
};

const RankCell = ({
	now,
	before,
}: {
	now: number | null;
	before: number | null;
}) => {
	const delta =
		before !== null || now !== null ? (before ?? 31) - (now ?? 31) : 0;
	return (
		<span className="tabular-nums">
			{rank(now)}
			{delta !== 0 && (
				<span
					className={`ml-1 text-[11px] ${delta > 0 ? "text-emerald-700" : "text-red-700"}`}
				>
					{delta > 0 ? "▲" : "▼"}
					{Math.abs(delta)}
				</span>
			)}
		</span>
	);
};

const TargetEditor = ({
	row,
	onSaved,
}: {
	row: KeywordRow;
	onSaved: () => void;
}) => {
	const [value, setValue] = useState(row.targetUrl ?? "");
	const [busy, setBusy] = useState(false);
	useEffect(() => setValue(row.targetUrl ?? ""), [row.targetUrl]);
	const dirty = value !== (row.targetUrl ?? "");
	const save = async () => {
		setBusy(true);
		try {
			await sendJson(`/competition/keywords/${row.id}`, "PATCH", {
				targetUrl: value.trim() || null,
			});
			onSaved();
		} finally {
			setBusy(false);
		}
	};
	return (
		<div className="flex items-center gap-1">
			<input
				className={`${input} py-1 font-mono text-[11px]`}
				placeholder="/kategori/…"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				onKeyDown={(e) => e.key === "Enter" && dirty && save()}
			/>
			{dirty && (
				<button
					type="button"
					className={btn.ghost}
					disabled={busy}
					onClick={save}
				>
					Save
				</button>
			)}
		</div>
	);
};

const KeywordsPanel = ({
	panel,
	setPanel,
	latestCheck,
}: {
	panel: PanelKey | "";
	setPanel: (p: PanelKey | "") => void;
	latestCheck: string | null;
}) => {
	const [list, setList] = useState<KeywordList | null>(null);
	const [q, setQ] = useState("");
	const [sort, setSort] = useState("volume");
	const [page, setPage] = useState(1);
	const [error, setError] = useState<string | null>(null);
	const [adding, setAdding] = useState({
		keyword: "",
		panel: "category",
		targetUrl: "",
	});

	const load = useCallback(async () => {
		setError(null);
		try {
			const params = new URLSearchParams({
				sort,
				page: String(page),
				limit: "50",
			});
			if (panel) params.set("panel", panel);
			if (q.trim()) params.set("q", q.trim());
			setList(await fetchJson<KeywordList>(`/competition/keywords?${params}`));
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}, [panel, q, sort, page]);

	useEffect(() => {
		const t = setTimeout(load, 250);
		return () => clearTimeout(t);
	}, [load]);

	const add = async () => {
		setError(null);
		const res = await sendJson<{ success: boolean; error?: string }>(
			"/competition/keywords",
			"POST",
			{
				keyword: adding.keyword,
				panel: adding.panel,
				targetUrl: adding.targetUrl || null,
			},
		);
		if (!res.success) return setError(res.error ?? "Could not add keyword");
		setAdding({ ...adding, keyword: "", targetUrl: "" });
		load();
	};

	return (
		<Panel
			title="Keywords"
			hint={
				latestCheck
					? `SERP checked ${latestCheck}, depth 30`
					: "not checked yet"
			}
		>
			<div className="mb-4 flex flex-wrap items-center gap-2">
				<select
					className={`${field} w-auto`}
					value={panel}
					onChange={(e) => {
						setPage(1);
						setPanel(e.target.value as PanelKey | "");
					}}
				>
					<option value="">All panels</option>
					{PANEL_KEYS.map((p) => (
						<option key={p} value={p}>
							{PANEL_LABELS[p]}
						</option>
					))}
				</select>
				<input
					className={`${field} w-56`}
					placeholder="Search keywords"
					value={q}
					onChange={(e) => {
						setPage(1);
						setQ(e.target.value);
					}}
				/>
				<select
					className={`${field} w-auto`}
					value={sort}
					onChange={(e) => setSort(e.target.value)}
				>
					<option value="volume">Search volume</option>
					<option value="rank">Our rank</option>
					<option value="pricerunner">PriceRunner rank</option>
					<option value="change">Biggest gain (4 weeks)</option>
					<option value="clicks">GSC clicks</option>
					<option value="keyword">A–Z</option>
				</select>
				{list && (
					<span className="ml-auto text-[12px] text-slate-500">
						{fmtNumber(list.total)} keywords
					</span>
				)}
			</div>
			<ErrorBanner error={error} />
			{list && (
				<div className="overflow-x-auto">
					<table className="w-full text-[12px]">
						<thead>
							<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
								<th className="py-2 pr-3 font-medium">Keyword</th>
								<th className="py-2 pl-3 text-right font-medium">Volume</th>
								<th className="py-2 pl-3 text-right font-medium">Our rank</th>
								<th className="py-2 pl-3 font-medium">Our page</th>
								<th className="py-2 pl-3 text-right font-medium">
									PriceRunner
								</th>
								<th className="py-2 pl-3 text-right font-medium">Grid</th>
								<th className="py-2 pl-3 text-right font-medium">GSC 28d</th>
								<th className="w-64 py-2 pl-3 font-medium">Target page</th>
							</tr>
						</thead>
						<tbody>
							{list.items.map((r) => (
								<tr
									key={r.id}
									className="border-b border-hairline align-top last:border-0"
								>
									<td className="py-2 pr-3">
										<div className="font-medium text-slate-700">
											{r.keyword}
										</div>
										<div className="text-[11px] text-slate-400">
											{PANEL_LABELS[r.panel]}
											{r.mainIntent ? ` · ${r.mainIntent}` : ""}
											{r.aiOverview ? " · AI Overview" : ""}
										</div>
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{fmtNumber(r.searchVolume)}
									</td>
									<td className="py-2 pl-3 text-right">
										{r.checkedOn ? (
											<RankCell now={r.ourRank} before={r.ourRankBefore} />
										) : (
											"—"
										)}
									</td>
									<td
										className={`max-w-56 truncate py-2 pl-3 font-mono text-[11px] ${r.ourUrlIsTarget === false ? "text-red-600" : r.ourUrlIsTarget ? "text-emerald-700" : "text-slate-500"}`}
										title={r.ourUrl ?? undefined}
									>
										{r.ourUrl ?? ""}
									</td>
									<td className="py-2 pl-3 text-right">
										{r.pricerunnerUrl && r.pricerunnerRank !== null ? (
											<a
												href={`https://www.pricerunner.dk${r.pricerunnerUrl}`}
												target="_blank"
												rel="noreferrer"
												className="tabular-nums text-primary hover:underline"
												title={r.pricerunnerUrl}
											>
												{rank(r.pricerunnerRank)}
											</a>
										) : r.checkedOn ? (
											"30+"
										) : (
											"—"
										)}
									</td>
									<td className="py-2 pl-3 text-right tabular-nums">
										{r.gridPresent
											? `${r.ourGridCards} / ${r.pricerunnerGridCards}`
											: "—"}
									</td>
									<td
										className="py-2 pl-3 text-right tabular-nums"
										title={r.gscTopUrl ?? undefined}
									>
										{r.gscImpressions
											? `${fmtNumber(r.gscClicks)} / ${compact(r.gscImpressions)} @ ${r.gscAvgPosition?.toFixed(1)}`
											: "—"}
									</td>
									<td className="py-2 pl-3">
										<TargetEditor row={r} onSaved={load} />
									</td>
								</tr>
							))}
						</tbody>
					</table>
					<div className="mt-3 flex items-center justify-between text-[12px] text-slate-500">
						<span>
							Grid is our cards / PriceRunner&apos;s. GSC is clicks /
							impressions @ average position. Our page turns green on the target
							page and red on any other.
						</span>
						<span className="flex items-center gap-2">
							<button
								type="button"
								className={btn.ghost}
								disabled={page <= 1}
								onClick={() => setPage(page - 1)}
							>
								Previous
							</button>
							{list.page} / {list.totalPages}
							<button
								type="button"
								className={btn.ghost}
								disabled={page >= list.totalPages}
								onClick={() => setPage(page + 1)}
							>
								Next
							</button>
						</span>
					</div>
				</div>
			)}
			<div className="mt-5 flex flex-wrap items-center gap-2 border-t border-hairline pt-4">
				<span className="text-[12px] font-medium text-slate-600">
					Track a keyword
				</span>
				<input
					className={`${field} w-56`}
					placeholder="keyword"
					value={adding.keyword}
					onChange={(e) => setAdding({ ...adding, keyword: e.target.value })}
				/>
				<select
					className={`${field} w-auto`}
					value={adding.panel}
					onChange={(e) => setAdding({ ...adding, panel: e.target.value })}
				>
					{PANEL_KEYS.map((p) => (
						<option key={p} value={p}>
							{PANEL_LABELS[p]}
						</option>
					))}
				</select>
				<input
					className={`${field} w-64 font-mono`}
					placeholder="target page (optional)"
					value={adding.targetUrl}
					onChange={(e) => setAdding({ ...adding, targetUrl: e.target.value })}
				/>
				<button
					type="button"
					className={btn.primary}
					disabled={!adding.keyword.trim()}
					onClick={add}
				>
					<Plus className="h-4 w-4" /> Add
				</button>
				<span className="text-[11px] text-slate-400">
					The panel is fixed once added, so before/after stays comparable.
				</span>
			</div>
		</Panel>
	);
};

const emptyInitiative = {
	workstream: "W1",
	name: "",
	panel: "",
	shippedAt: "",
	urlPattern: "",
	notes: "",
};

const InitiativesPanel = ({
	initiatives,
	onChanged,
}: {
	initiatives: Initiative[];
	onChanged: () => void;
}) => {
	const [draft, setDraft] = useState(emptyInitiative);
	const [error, setError] = useState<string | null>(null);

	const create = async () => {
		setError(null);
		const res = await sendJson<{ success: boolean; error?: string }>(
			"/competition/initiatives",
			"POST",
			{
				...draft,
				panel: draft.panel || null,
				shippedAt: draft.shippedAt || null,
			},
		);
		if (!res.success) return setError(res.error ?? "Could not save");
		setDraft(emptyInitiative);
		onChanged();
	};

	const patch = async (id: number, body: Record<string, unknown>) => {
		const res = await sendJson<{ success: boolean; error?: string }>(
			`/competition/initiatives/${id}`,
			"PATCH",
			body,
		);
		if (!res.success) setError(res.error ?? "Could not save");
		onChanged();
	};

	const remove = async (id: number) => {
		await sendJson(`/competition/initiatives/${id}`, "DELETE");
		onChanged();
	};

	return (
		<Panel
			title="Initiatives"
			hint="A shipped date puts a marker on the trend and starts the 4/8/12-week readout"
		>
			<ErrorBanner error={error} />
			{initiatives.length > 0 ? (
				<table className="mb-4 w-full text-[13px]">
					<thead>
						<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
							<th className="py-2 pr-3 font-medium">Work</th>
							<th className="py-2 pr-3 font-medium">Initiative</th>
							<th className="py-2 pr-3 font-medium">Panel</th>
							<th className="py-2 pr-3 font-medium">Shipped</th>
							<th className="py-2 pr-3 font-medium">Readouts</th>
							<th />
						</tr>
					</thead>
					<tbody>
						{initiatives.map((i) => (
							<tr key={i.id} className="border-b border-hairline last:border-0">
								<td className="py-2 pr-3 font-medium">{i.workstream}</td>
								<td className="py-2 pr-3">
									<div>{i.name}</div>
									{(i.urlPattern || i.notes) && (
										<div className="text-[11px] text-slate-400">
											{[i.urlPattern, i.notes].filter(Boolean).join(" · ")}
										</div>
									)}
								</td>
								<td className="py-2 pr-3 text-[12px]">
									{i.panel ? PANEL_LABELS[i.panel] : "—"}
								</td>
								<td className="py-2 pr-3">
									<input
										type="date"
										className={`${field} w-40 py-1`}
										value={i.shippedAt ?? ""}
										onChange={(e) =>
											patch(i.id, { shippedAt: e.target.value || null })
										}
									/>
								</td>
								<td className="py-2 pr-3 text-[11px] text-slate-500">
									{i.shippedAt
										? [4, 8, 12]
												.map((w) => {
													const d = new Date(`${i.shippedAt}T00:00:00Z`);
													d.setUTCDate(d.getUTCDate() + w * 7);
													return `${w}w ${fmtShortDate(d.toISOString().slice(0, 10))}`;
												})
												.join(" · ")
										: "not shipped"}
								</td>
								<td className="py-2 text-right">
									<button
										type="button"
										className="text-slate-400 hover:text-danger"
										onClick={() => remove(i.id)}
										aria-label="Delete initiative"
									>
										<Trash2 className="h-4 w-4" />
									</button>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			) : (
				<div className="mb-4">
					<Empty>Nothing logged yet. Add each piece of work as it ships.</Empty>
				</div>
			)}
			<div className="grid gap-2 md:grid-cols-[90px_1fr_190px_160px_1fr_auto]">
				<select
					className={input}
					value={draft.workstream}
					onChange={(e) => setDraft({ ...draft, workstream: e.target.value })}
				>
					{["W1", "W2", "W3", "W4", "W5", "W6", "W7", "W8", "W9", "M5"].map(
						(w) => (
							<option key={w}>{w}</option>
						),
					)}
				</select>
				<input
					className={input}
					placeholder="What shipped"
					value={draft.name}
					onChange={(e) => setDraft({ ...draft, name: e.target.value })}
				/>
				<select
					className={input}
					value={draft.panel}
					onChange={(e) => setDraft({ ...draft, panel: e.target.value })}
				>
					<option value="">No panel</option>
					{PANEL_KEYS.filter((p) => p !== "control").map((p) => (
						<option key={p} value={p}>
							{PANEL_LABELS[p]}
						</option>
					))}
				</select>
				<input
					type="date"
					className={input}
					value={draft.shippedAt}
					onChange={(e) => setDraft({ ...draft, shippedAt: e.target.value })}
				/>
				<input
					className={`${input} font-mono`}
					placeholder="URL pattern, e.g. /produkter/*"
					value={draft.urlPattern}
					onChange={(e) => setDraft({ ...draft, urlPattern: e.target.value })}
				/>
				<button
					type="button"
					className={btn.primary}
					disabled={!draft.name.trim()}
					onClick={create}
				>
					<Plus className="h-4 w-4" /> Add
				</button>
			</div>
		</Panel>
	);
};

const Competition = () => {
	const [overview, setOverview] = useState<CompetitionOverview | null>(null);
	const [sections, setSections] = useState<SectionsReport | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(true);
	const [panel, setPanel] = useState<PanelKey | "">("");

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [o, s] = await Promise.all([
				fetchJson<CompetitionOverview>("/competition/overview"),
				fetchJson<SectionsReport>("/competition/sections"),
			]);
			setOverview(o);
			setSections(s);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	const w = overview?.windows;

	return (
		<div className="space-y-6">
			<PageHeader
				kicker="Monitoring · Competition"
				title="PriceOnline against PriceRunner and Prisjagt."
				qualifier={`Since ${w?.baselineDate ?? "2026-10-28"}.`}
				description={
					<p>
						Domain figures are DataForSEO estimates. Panels are our own weekly
						checks of fixed keyword sets, and a panel&apos;s lift is its change
						in top-10 share minus the control panel&apos;s, so season and Black
						Friday cancel out. See COMPETITOR-GAP-PLAN.md.
					</p>
				}
				actions={
					<>
						{overview && (
							<span className="text-[11px] text-zinc-400">
								Updated {fmtDateTime(overview.generatedAt)}
							</span>
						)}
						<button
							type="button"
							className={btn.ghost}
							disabled={loading}
							onClick={load}
						>
							<RefreshCw
								className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
							/>
							Refresh
						</button>
					</>
				}
			/>

			<ErrorBanner error={error} />
			{!overview && loading && <Spinner />}

			{overview && (
				<>
					<div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
						{METRICS.map((m) => (
							<ScoreCard key={m.key} metric={m} domains={overview.domains} />
						))}
					</div>

					<Panel
						title="Keyword panels"
						hint={
							w?.firstSerpCheck
								? `First check ${w.firstSerpCheck} → latest ${w.latestSerpCheck}. Click a row to list its keywords.`
								: "No SERP checks yet"
						}
					>
						<PanelTable rows={overview.panels} onPick={setPanel} />
					</Panel>

					<Panel
						title="Top-10 share per panel, weekly"
						hint="Dashed lines are shipped initiatives"
					>
						<TrendChart overview={overview} />
					</Panel>

					{sections && (
						<Panel title="Search Console by section" hint="Clicks per week">
							{sections.rows.length > 0 ? (
								<SectionsPanel report={sections} />
							) : (
								<Empty>
									No rollup yet. It fills from the next Search Console ingest.
								</Empty>
							)}
						</Panel>
					)}

					<KeywordsPanel
						panel={panel}
						setPanel={setPanel}
						latestCheck={w?.latestSerpCheck ?? null}
					/>

					<InitiativesPanel
						initiatives={overview.initiatives}
						onChanged={load}
					/>
				</>
			)}
		</div>
	);
};

export default Competition;
